import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import cardIcon from '../assets/card_icon.png';
import ssalsoongiIcon from '../assets/ssalsoongi_icon.png';

const CHOICES = ['rock', 'paper', 'scissors'];

export default function RaidGame({ boss, onClose, onGameEnd }) {
  const [round, setRound] = useState(1);
  const [gameState, setGameState] = useState('ready'); // 'ready' | 'playing' | 'result'

  const [userScore, setUserScore] = useState(0);
  const [bossScore, setBossScore] = useState(0);

  const [userChoice, setUserChoice] = useState(null);
  const [bossChoice, setBossChoice] = useState(null);
  const [roundResult, setRoundResult] = useState(null);

  const [finalResult, setFinalResult] = useState(null);
  const [rewards, setRewards] = useState({
    cardIndex: null,
    cardName: null,
    cardGrade: null,
    ssiIndex: null,
    ssiQuantity: 0,
  });
  const [loading, setLoading] = useState(false);

  // 기존 전투 기록(오늘자 raid_entries) 로드
  useEffect(() => {
    const loadExistingState = async () => {
      try {
        setLoading(true);
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        if (!session?.access_token) {
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.functions.invoke('play-raid-round', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: {
            bossId: boss.id,
            // userChoice 생략 → 서버가 상태만 반환하도록 구현
          },
        });

        if (error) {
          console.error('play-raid-round (load) error:', error);
          setLoading(false);
          return;
        }

        const {
          currentRound,
          userScore: serverUserScore,
          bossScore: serverBossScore,
          matchResult,
        } = data || {};

        setRound(currentRound ?? 1);
        setUserScore(serverUserScore ?? 0);
        setBossScore(serverBossScore ?? 0);

        if (matchResult) {
          // 이미 끝난 전투면 결과 화면으로
          setFinalResult(matchResult);
          setGameState('result');
        } else {
          setGameState('ready');
        }
      } catch (e) {
        console.error('Failed to load raid state:', e);
      } finally {
        setLoading(false);
      }
    };

    loadExistingState();
  }, [boss.id]);

  const handleChoice = async (choice) => {
    if (gameState !== 'ready' || loading) return;

    setGameState('playing');
    setUserChoice(choice);
    setRoundResult(null);

    try {
      setLoading(true);

      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session?.access_token) {
        alert('Login required.');
        setGameState('ready');
        return;
      }

      const { data, error } = await supabase.functions.invoke('play-raid-round', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          bossId: boss.id,
          userChoice: choice, // 라운드/점수/종료 여부는 서버가 판단
        },
      });

      if (error) {
        console.error('play-raid-round error:', error);
        alert('Failed to play raid game. Please try again.');
        setGameState('ready');
        return;
      }

      console.log('play-raid-round result:', data);

      const {
        serverChoice,
        roundResult: result,
        currentRound,
        userScore: serverUserScore,
        bossScore: serverBossScore,
        matchResult, // null | 'win' | 'lose' | 'draw'
        rewardedCardIndex,
        rewardedCardName,
        rewardedCardGrade,
        rewardedSsiIndex,
        rewardedSsiQuantity,
      } = data || {};

      setBossChoice(serverChoice);
      setRoundResult(result);
      setUserScore(serverUserScore ?? 0);
      setBossScore(serverBossScore ?? 0);
      setRound(currentRound ?? 1);

      if (matchResult) {
        // 서버가 매치 종료까지 판정함
        setFinalResult(matchResult);
        setRewards({
          cardIndex: rewardedCardIndex,
          cardName: rewardedCardName,
          cardGrade: rewardedCardGrade,
          ssiIndex: rewardedSsiIndex,
          ssiQuantity: rewardedSsiQuantity,
        });
        setGameState('result');
      } else {
        // 다음 라운드 준비
        setTimeout(() => {
          setGameState('ready');
        }, 1200);
      }
    } catch (e) {
      console.error('Failed to call play-raid-round:', e);
      alert('Failed to play raid game. Please try again.');
      setGameState('ready');
    } finally {
      setLoading(false);
    }
  };

  const getEmoji = (choice) => {
    if (choice === 'rock') return '✊';
    if (choice === 'paper') return '✋';
    if (choice === 'scissors') return '✌️';
    return '❓';
  };

  const isGameFinished = gameState === 'result';

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        flexDirection: 'column',
        padding: 20,
        boxSizing: 'border-box',
        position: 'relative', // 오버레이 기준
        overflow: 'hidden',
      }}
    >
      {/* Header / Close */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 24,
            cursor: 'pointer',
            opacity: 0.6,
          }}
        >
          ✕
        </button>
      </div>

      {/* Boss Section */}
      <div
        className="animate-pop"
        style={{
          background: '#fff',
          borderRadius: 24,
          padding: 20,
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 80,
            background: 'linear-gradient(to bottom, #ffe2e2, #fff)',
            opacity: 0.5,
            zIndex: 0,
          }}
        />

        <div style={{ zIndex: 1, textAlign: 'center', width: '100%' }}>
          <div
            style={{
              width: 120,
              height: 120,
              margin: '12px auto 12px',
              borderRadius: '50%',
              border: '4px solid #fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              background: '#f0f0f0',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <img
              src={boss.bgImage || boss.image}
              alt={boss.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#2d3436' }}>{boss.name}</h2>
          <p style={{ margin: 0, color: '#636e72', fontSize: 13, fontWeight: 500 }}>
            Round <span style={{ color: '#0984e3' }}>{round}</span>
          </p>
        </div>
      </div>

      {/* Score Board */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 40,
          marginBottom: 30,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: '#636e72',
              paddingBottom: 4,
            }}
          >
            You
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: userScore > bossScore ? '#00b894' : '#2d3436',
              minWidth: 40,
            }}
          >
            {userScore}
          </div>
        </div>

        <div style={{ width: 1, background: '#dfe6e9', height: 40 }} />

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: '#636e72',
              paddingBottom: 4,
            }}
          >
            Boss
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: bossScore > userScore ? '#d63031' : '#2d3436',
              minWidth: 40,
            }}
          >
            {bossScore}
          </div>
        </div>
      </div>

      {/* Battle Area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 20,
          marginBottom: 30,
        }}
      >
        {/* User Move */}
        <div
          style={{
            width: 90,
            height: 90,
            background: '#fff',
            borderRadius: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 48,
            boxShadow: userChoice
              ? '0 8px 20px rgba(9, 132, 227, 0.2)'
              : 'inset 0 0 0 2px #f0f0f0',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            transform: userChoice ? 'scale(1.05)' : 'scale(1)',
            border: userChoice ? '2px solid #0984e3' : 'none',
          }}
        >
          {userChoice ? getEmoji(userChoice) : <span style={{ fontSize: 24, opacity: 0.2 }}>?</span>}
        </div>

        <div style={{ fontSize: 24, fontWeight: 900, color: '#b2bec3' }}>VS</div>

        {/* Boss Move */}
        <div
          style={{
            width: 90,
            height: 90,
            background: '#fff',
            borderRadius: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 48,
            boxShadow: bossChoice
              ? '0 8px 20px rgba(214, 48, 49, 0.2)'
              : 'inset 0 0 0 2px #f0f0f0',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            transform: bossChoice ? 'scale(1.05)' : 'scale(1)',
            border: bossChoice ? '2px solid #d63031' : 'none',
          }}
        >
          {bossChoice ? getEmoji(bossChoice) : <span style={{ fontSize: 24, opacity: 0.2 }}>?</span>}
        </div>
      </div>

      {/* Result Text Overlay or Center */}
      <div style={{ height: 40, textAlign: 'center', marginBottom: 20 }}>
        {roundResult && !isGameFinished && (
          <span
            key={round} // animate on new round
            className="animate-pop"
            style={{
              display: 'inline-block',
              padding: '8px 24px',
              borderRadius: 20,
              background:
                roundResult === 'win'
                  ? '#00b894'
                  : roundResult === 'lose'
                  ? '#d63031'
                  : '#f0932b',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {roundResult === 'win' ? 'WIN!' : roundResult === 'lose' ? 'LOSE!' : 'DRAW!'}
          </span>
        )}
      </div>

      {/* Controls (게임 진행 중에만 버튼 보이게) */}
      {!isGameFinished && (
        <div style={{ paddingBottom: 20 }}>
          <p
            style={{
              textAlign: 'center',
              color: '#b2bec3',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {gameState === 'playing' ? 'Opponent is thinking...' : 'Choose your move'}
          </p>
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              pointerEvents: gameState === 'playing' || loading ? 'none' : 'auto',
              opacity: gameState === 'playing' || loading ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {CHOICES.map((choice) => (
              <button
                key={choice}
                onClick={() => handleChoice(choice)}
                style={{
                  flex: 1,
                  maxWidth: 90,
                  height: 60,
                  borderRadius: 16,
                  border: 'none',
                  background: '#fff',
                  fontSize: 28,
                  cursor: 'pointer',
                  boxShadow: '0 4px 0 #dfe6e9',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(4px)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 0 #dfe6e9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 0 #dfe6e9';
                }}
              >
                {getEmoji(choice)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 결과 오버레이: 게임이 끝났을 때 RaidGame 위에 덮어쓰기 */}
      {isGameFinished && (
        <div
          className="animate-pop"
          style={{
            position: 'absolute',
            inset: 0, // top:0, right:0, bottom:0, left:0
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 360,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              padding: 24,
              borderRadius: 24,
              boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>
              {finalResult === 'win' ? '🏆' : finalResult === 'lose' ? '💀' : '🤝'}
            </div>
            <h2
              style={{
                color:
                  finalResult === 'win'
                    ? '#00b894'
                    : finalResult === 'lose'
                    ? '#d63031'
                    : '#f0932b',
                fontSize: 28,
                margin: '0 0 8px',
                fontFamily: 'Montserrat, sans-serif', // Assuming nice font or default
                fontWeight: 800,
              }}
            >
              {finalResult === 'win'
                ? 'VICTORY!'
                : finalResult === 'lose'
                ? 'DEFEAT'
                : 'DRAW'}
            </h2>
            <p style={{ color: '#636e72', marginBottom: 24 }}>
              {finalResult === 'win' ? 'You defeated the boss!' : 'Better luck next time.'}
            </p>

            {(rewards.cardIndex !== null || rewards.ssiQuantity > 0) && (
              <div
                style={{
                  marginBottom: 24,
                  padding: 16,
                  background: '#f1f2f6',
                  borderRadius: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#2d3436',
                    marginBottom: 4,
                  }}
                >
                  REWARDS
                </div>

                {rewards.cardIndex !== null && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: '#fff',
                      padding: 10,
                      borderRadius: 12,
                    }}
                  >
                    <img
                      src={cardIcon}
                      alt="Card"
                      style={{ width: 40, height: 40, borderRadius: 8 }}
                    />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>Card</div>
                      <div style={{ fontSize: 12, color: '#636e72' }}>
                        {rewards.cardName || 'Card'} -{' '}
                        {rewards.cardGrade || `Index: ${rewards.cardIndex}`}
                      </div>
                    </div>
                  </div>
                )}

                {rewards.ssiQuantity > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: '#fff',
                      padding: 10,
                      borderRadius: 12,
                    }}
                  >
                    <img
                      src={ssalsoongiIcon}
                      alt="Pet"
                      style={{ width: 40, height: 40, borderRadius: 8 }}
                    />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>Pet</div>
                      <div style={{ fontSize: 12, color: '#636e72' }}>
                        x {rewards.ssiQuantity}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => {
                if (finalResult) onGameEnd(finalResult);
              }}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: 16,
                fontWeight: 700,
                color: '#fff',
                background: '#2d3436',
                border: 'none',
                borderRadius: 16,
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(45, 52, 54, 0.2)',
                transition: 'transform 0.1s',
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
              onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              Return to List
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
